// src/com/auto_using_car_info/AutoUsingCarInfoInput.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { Button, DialogContent, DialogActions, Grid, CircularProgress } from '@mui/material';
import { AppWord, AppObject, AppStyle } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import VzmIntField from '../../common/VzmIntField';
import { VzmPrint } from '../../common/VzmStyled';
import { useLazyQuery } from '@apollo/client';
import InputUtil from '../../value/InputUtil';
import { AutoUsingCarInfoDictionary } from './AutoUsingCarInfoDict';
import { useAlertSetter } from '../../api/app-hook';
import ValueMisc from '../../value/ValueMisc';
import { LIST_AUTO_USING_CAR_INFO_TO_EDIT, LIST_AUTO_USING_CAR_INFO_LATEST } from './AutoUsingCarInfoGql';

const HeadCell = styled(Grid)({display:'flex', alignItems:'center', padding:10, justifyContent: 'center' });
const Cell = styled(Grid)({display:'flex', alignItems:'center', padding:3, justifyContent: 'center' });
const InputMap = AutoUsingCarInfoDictionary.inputMap;

/**
 * (AUTO_USING_CAR_INFO) 정보 생성 또는 편집 Dialog
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-10-24 13:19:58 using DbdbTap
 */
export default function AutoUsingCarInfoInput({
    open,
    dcCd,
    planDate,
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const [inputList, setInputList] = useState([]);
    const [changeCount, setChangeCount] = useState(0);
    const [dcName, setDcName] = useState('');
    const [needLatest, setNeedLatest] = useState(false);
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.
    const requestRef = useRef(0);

    const [getAuthUsingCarInfoListToEdit, responseList] = useLazyQuery(LIST_AUTO_USING_CAR_INFO_TO_EDIT, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetAuthUsingCarInfoListToEdit(data, option)},
        onError: (error) => addAlert({title: '가용차량 설정 기초정보 조회 오류', message: [error.message]})
    });

    const [getLatestList, responseLatest] = useLazyQuery(LIST_AUTO_USING_CAR_INFO_LATEST, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetLatestList(data, option)},
        onError: (error) => addAlert({title: '최신 가용차량 설정 정보 조회 오류', message: [error.message]})
    });

    useEffect(()=>{
        if(open && dcCd && planDate) {
            if(requestRef.current === 0) {
                requestRef.current++;
                getAuthUsingCarInfoListToEdit({variables:{dcCd, planDate}});
            }
            else requestRef.current = 0;
        }
        else requestRef.current = 0;
    }, [open, dcCd, planDate]);

    const onCompleteGetAuthUsingCarInfoListToEdit = (data, option) => {
        if(data.autoUsingCarInfoListToEdit) {
            // 변경정보가 있는지 확인하기 위해 기존 값을 저장.
            let lines = data.autoUsingCarInfoListToEdit.map((item) => {
                item.oldUsingCarCnt = item.usingCarCnt;
                return item;
            });
            setInputList(lines);
            setChangeCount(0);

            let youNeedLatest = false;

            if(lines.length >0) {
                setDcName(lines[0].dcName);
                const ymdToday = ValueMisc.getYMD(new Date());

                if(ymdToday <= planDate) {
                    const sumUsingCount = lines.reduce((acc, cur) => acc + cur.usingCarCnt, 0);
                    if(sumUsingCount===0) youNeedLatest = true;
                }
            }

            setNeedLatest(youNeedLatest);
        }
    };

    const onCompleteGetLatestList = (data, option) => {
        if(data.autoUsingCarInfoLatestList) {
            // 마지막 날짜의 가용차량 수를 가져와서 inputList에 적용.
            const lines = [...inputList];
            const latestMap = data.autoUsingCarInfoLatestList.reduce((acc, cur) => {
                acc[cur.vehTypeId] = cur.usingCarCnt;
                return acc;
            }, {});

            for(let i=0; i<lines.length; i++) {
                if(latestMap[lines[i].vehTypeId]) {
                    lines[i].usingCarCnt = latestMap[lines[i].vehTypeId];
                }
            }
            setInputList(lines);
            setChangeCount(getCountOfChanged(lines));
            setNeedLatest(false);
        }
    };

    const getCountOfChanged = (inputLines) => {
        let count = inputLines.reduce((acc, cur) => {
            return acc + (cur.usingCarCnt !== cur.oldUsingCarCnt ? 1 : 0);
        }, 0);
        return count;
    };

    const onClickRestore = () => {
        const lines = inputList.map((item) => {
            item.usingCarCnt = item.oldUsingCarCnt;
            return item;
        });

        const ymdToday = ValueMisc.getYMD(new Date());

        if(ymdToday <= planDate) {
            const sumUsingCount = lines.reduce((acc, cur) => acc + cur.usingCarCnt, 0);
            if(sumUsingCount===0) setNeedLatest(true);
        }

        setInputList(lines);
        setChangeCount(0);
    };

    // 특정 차종에 대한 가용차량 수를 변경.
    // 목록에서 해당 차종을 찾아서 가용차량 수를 변경.
    const onChangeCount = (vehTypeId, count) => {
        const lines = [...inputList];
        for(let i=0; i<lines.length; i++) {
            if(lines[i].vehTypeId === vehTypeId) {
                lines[i].usingCarCnt = count;
                break;
            }
        }
        setInputList(lines);
        // setChanged(getCountOfChanged(lines));
        setChangeCount(getCountOfChanged(lines));
    };

    const onClickSubmit = () => {
        if(!getCountOfChanged(inputList)) {
            addAlert({title: '변경사항 없음', message: ['가용차량 수 변경사항이 없습니다.']});
            // setChanged(false);
            setChangeCount(0);
            return;
        }
        // const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        const submitList = inputList.map((item) => {
            return InputUtil.getDataToSubmit(item, InputMap);
        });
        onRequestSave(submitList);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(changeCount > 0) askCancel();
        else onClose();
    };

    const loadingAny = loadingSave || responseList.loading || responseLatest.loading;

    const renderInputLine = (item, index) => {
        return(
            <React.Fragment key={item.vehTypeId}>
                <Cell item xs={2}>
                    <Box style={{padding:3, textAlign:'center', flexGrow:1}}>
                        {index + 1}
                    </Box>
                </Cell>
                <Cell item xs={4}>{item.vehTypeName}</Cell>
                <Cell item xs={3}>{item.vehTypeAlias}</Cell>
                <Cell item xs={3}>
                    <VzmIntField
                        dict={AutoUsingCarInfoDictionary.inputMap.usingCarCnt}
                        value={item.usingCarCnt}
                        onChange={(id, value, error) => onChangeCount(item.vehTypeId, value)}
                    />
                </Cell>
            </React.Fragment>
        );
    };

    const renderInputList = () => {
        return(
            <Box
                style={{
                    width:500, minHeight:200, display:'flex', flexDirection:'column',
                    border: AppStyle.BorderCCC, borderRadius:5
                }}
            >
                <Box
                    style={{backgroundColor:'#eee'}}
                >
                    <Grid container>
                        <HeadCell item xs={2}>No.</HeadCell>
                        <HeadCell item xs={4}>차종</HeadCell>
                        <HeadCell item xs={3}>차종별칭</HeadCell>
                        <HeadCell item xs={3}>가용차량 수</HeadCell>
                    </Grid>
                </Box>
                <Box
                    style={{maxHeight:'30vh', flexGrow:1, overflowY:'auto', fontSize:'0.85rem'}}
                >
                    <Grid container>
                        {
                            inputList.map((item, index) => renderInputLine(item, index))
                        }
                    </Grid>
                </Box>
            </Box>
        );
    };

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='md'
            title='차종별 가용차량 수 일괄 설정'
        >
            <DialogContent>
                {
                    inputList.length === 0
                    ?
                    <Box width={400} height={300}
                        style={{display:'flex', justifyContent:'center', alignItems:'center'}}
                    >
                        <Box style={{color:'#f77'}}>
                            해당 센터에 등록된 차종이 없어서<br/>
                            가용차량 수를 설정할 수 없습니다.
                        </Box>
                    </Box>
                    :
                    <Box>
                        <Grid container spacing={1} style={{marginBottom:5}}>
                            <Grid item xs={6}>
                                <VzmPrint label="물류센터" value={dcName} />
                            </Grid>
                            <Grid item xs={6}>
                                <VzmPrint label="배차일자" value={planDate} />
                            </Grid>
                        </Grid>
                        {renderInputList()}
                    </Box>
                }
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={()=>getLatestList({variables:{dcCd}})}
                            disabled={!needLatest}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.secondary}
                        >
                            최신 정보 채워넣기
                        </Button>
                        <Button
                            onClick={onClickRestore}
                            disabled={changeCount < 1 || loadingAny}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.inherit}
                        >
                            모두 되돌리기
                        </Button>
                        <Button
                            onClick={onClickSubmit}
                            disabled={changeCount < 1 || loadingAny}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
